import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import EventIcon from "@material-ui/icons/Event";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PeopleIcon from "@material-ui/icons/People";
import "firebase/firestore";
import { Link } from "react-router-dom";
import useStore from "../shared/store";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";

// ─── Greeting helper ──────────────────────────────────────────────────────────
const getTimeOfDayGreeting = () => {
	const hour = new Date().getHours();
	if (hour < 12) return "Good Morning";
	if (hour < 18) return "Good Afternoon";
	return "Good Evening";
};

// ─── Font injection — must be a <link> tag, not @import inside a style tag.
// @import inside dynamically injected <style> elements is unreliable across browsers.
const injectFont = () => {
	if (document.getElementById("dashboard-font")) return;
	const link = document.createElement("link");
	link.id = "dashboard-font";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap";
	document.head.appendChild(link);
};

// ─── Style injection — no @import here ────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById("dashboard-styles")) return;
	const style = document.createElement("style");
	style.id = "dashboard-styles";
	style.textContent = `
		.db-wrap, .db-wrap *, .db-wrap *::before, .db-wrap *::after {
			box-sizing: border-box;
		}
		.db-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			background: #F5F3F0;
		}

		/* ── Date bar ── */
		.db-datebar {
			background: #ffffff;
			border-bottom: 1px solid rgba(0,0,0,0.07);
			padding: 13px 40px;
			display: flex;
			justify-content: flex-end;
		}
		.db-datebar__text {
			font-family: 'Manrope', sans-serif;
			font-size: 14px;
			font-weight: 600;
			color: #18181A;
			letter-spacing: -0.01em;
		}

		/* ── Greeting banner ── */
		.db-greeting {
			background: linear-gradient(130deg, #F4A96A 0%, #E47029 45%, #B94D1A 100%);
			padding: 40px 40px 48px;
			position: relative;
			overflow: hidden;
		}
		.db-greeting::before {
			content: '';
			position: absolute;
			width: 380px; height: 380px;
			border-radius: 50%;
			border: 1.5px solid rgba(255,255,255,0.12);
			right: -120px; top: -120px;
		}
		.db-greeting::after {
			content: '';
			position: absolute;
			width: 200px; height: 200px;
			border-radius: 50%;
			border: 1.5px solid rgba(255,255,255,0.12);
			left: -60px; bottom: -80px;
		}
		.db-greeting__inner {
			position: relative;
			z-index: 1;
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
		.db-greeting__name {
			font-family: 'Manrope', sans-serif;
			font-size: 32px;
			font-weight: 800;
			color: #ffffff;
			letter-spacing: -0.02em;
			margin-bottom: 8px;
			line-height: 1.1;
		}
		.db-greeting__sub {
			font-size: 15px;
			color: rgba(255,255,255,0.85);
		}
		.db-greeting__sub strong {
			color: #ffffff;
			font-weight: 600;
		}
		.db-greeting__badge {
			background: rgba(255,255,255,0.17);
			border: 1px solid rgba(255,255,255,0.22);
			border-radius: 14px;
			padding: 18px 28px;
			text-align: center;
			min-width: 110px;
			flex-shrink: 0;
		}
		.db-greeting__badge-num {
			font-family: 'Manrope', sans-serif;
			font-size: 40px;
			font-weight: 800;
			color: #ffffff;
			line-height: 1;
		}
		.db-greeting__badge-label {
			font-size: 11px;
			color: rgba(255,255,255,0.7);
			text-transform: uppercase;
			letter-spacing: 0.06em;
			margin-top: 5px;
		}

		/* ── Section title ── */
		.db-section-header {
			padding: 28px 40px 0;
			display: flex;
			align-items: center;
			gap: 14px;
		}
		.db-section-header__title {
			font-family: 'Manrope', sans-serif;
			font-size: 28px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.02em;
			white-space: nowrap;
		}
		.db-section-header__line {
			flex: 1;
			height: 2px;
			background: linear-gradient(90deg, rgba(228,112,41,0.45) 0%, transparent 100%);
			border-radius: 2px;
		}

		/* ── Cards grid ── */
		.db-cards-wrap {
			padding: 20px 40px 40px;
		}

		/* ── Individual card ── */
		.db-card {
			background: #ffffff;
			border: 1.5px solid rgba(0,0,0,0.06);
			border-radius: 18px;
			padding: 26px 22px 22px;
			cursor: pointer;
			transition: box-shadow 0.22s ease, transform 0.18s ease, border-color 0.2s ease;
			display: flex;
			flex-direction: column;
			min-height: 200px;
			height: 100%;
			box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px -4px rgba(0,0,0,0.06);
			text-decoration: none;
			color: inherit;
		}
		.db-card:hover {
			box-shadow: 0 4px 8px rgba(0,0,0,0.04), 0 16px 36px -12px rgba(228,112,41,0.28);
			transform: translateY(-4px);
			border-color: #F5C5A0;
		}
		.db-card__icon {
			width: 50px;
			height: 50px;
			border-radius: 14px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 18px;
			flex-shrink: 0;
		}
		.db-card__title {
			font-family: 'Manrope', sans-serif;
			font-size: 15.5px;
			font-weight: 700;
			color: #18181A;
			margin-bottom: 6px;
			line-height: 1.3;
		}
		.db-card__sub {
			font-size: 12.5px;
			color: #9A9A9F;
			line-height: 1.5;
			flex: 1;
		}
		.db-card__badge {
			display: inline-flex;
			align-items: center;
			margin-top: 16px;
			font-size: 11.5px;
			font-weight: 700;
			padding: 5px 11px;
			border-radius: 20px;
			background: #FEF0E7;
			color: #D45F1A;
			white-space: nowrap;
			width: fit-content;
			letter-spacing: 0.01em;
		}
		.db-card__badge-dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: #E47029;
			margin-right: 6px;
			flex-shrink: 0;
		}
		.db-card__cta {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			margin-top: 16px;
			font-size: 13px;
			font-weight: 700;
			color: #E47029;
			text-decoration: none;
		}
		.db-card__cta-arrow {
			transition: transform 0.15s ease;
		}
		.db-card:hover .db-card__cta-arrow {
			transform: translate(2px, -2px);
		}
	`;
	document.head.appendChild(style);
};

// ─── Arrow icon ───────────────────────────────────────────────────────────────
const ArrowIcon = () => (
	<svg
		className="db-card__cta-arrow"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		width="14"
		height="14"
		aria-hidden="true"
	>
		<line x1="7" y1="17" x2="17" y2="7" />
		<polyline points="7,7 17,7 17,17" />
	</svg>
);

// ─── Role display helper ──────────────────────────────────────────────────────
const getDisplayName = (user) => {
	if (!user) return "";
	if (user.contact_name) return user.contact_name;
	const role = user.roles?.[0];
	if (role === "admin") return "Admin";
	if (role === "healthOrgAdmin") return "HAdmin";
	if (role === "oa") return "MOA";
	if (role === "doctor") return "Doctor";
	return "";
};

// ─── Main component ───────────────────────────────────────────────────────────
const Dashboard = ({ smallScreenView, handleMenuClick }) => {
	const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
	const [totalMessagesCount, setTotalMessagesCount] = useState(0);
	const { org, user } = useStore();
	const emrURL = org?.emr?.emrURL;
	const year = new Date().getFullYear();
	const month = new Date().getMonth();
	const day = new Date().getDate();

	// Inject font and styles once on mount — separate from user dependency
	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const getUnreadMessagesCount = async () => {
		try {
			const userId = user._id;
			const orgId = user.orgs[0];
			const result = await api(
				apiConfigs.baseOption("get", `providerMessages/${orgId}_${userId}`)
			);
			let unreadCount = 0;
			if (result?.data?.messages?.length) {
				result.data.messages.forEach((message) => {
					if (!message.read) unreadCount += 1;
				});
			}
			setUnreadMessagesCount(unreadCount);
			setTotalMessagesCount(result?.data?.messages?.length || 0);
		} catch (error) {
			setUnreadMessagesCount(0);
			setTotalMessagesCount(0);
		}
	};

	useEffect(() => {
		if (user) {
			getUnreadMessagesCount();
		}
	}, [user]);

	// Guard — don't render until user is available
	if (!user) return null;

	return (
		<div className="db-wrap">

			{/* Date bar */}
			<div className="db-datebar">
				<span className="db-datebar__text">
					{new Date().toLocaleDateString([], {
						year: "numeric",
						month: "long",
						day: "numeric",
					})}
				</span>
			</div>

			{/* Greeting banner */}
			<div className="db-greeting">
				<div className="db-greeting__inner">
					<div>
						<div className="db-greeting__name">
							{getTimeOfDayGreeting()}, {getDisplayName(user)}
						</div>
						<div className="db-greeting__sub">
							You have{" "}
							<strong>{unreadMessagesCount} unread messages</strong> today
						</div>
					</div>
					<div className="db-greeting__badge">
						<div className="db-greeting__badge-num">{unreadMessagesCount}</div>
						<div className="db-greeting__badge-label">Unread</div>
					</div>
				</div>
			</div>

			{/* Dashboard title */}
			<div className="db-section-header">
				<span className="db-section-header__title">Dashboard</span>
				<div className="db-section-header__line" />
			</div>

			{/* Cards */}
			<div className="db-cards-wrap">
				<Grid container spacing={2}>

					<Grid item xs={12} md={6} lg={3}>
						<Link to="/MessagePage" className="db-card">
							<div className="db-card__icon">
								<PeopleIcon style={{ fontSize: 24, color: "#E47029" }} />
							</div>
							<div className="db-card__title">Documents Delivered</div>
							<div className="db-card__sub">
								Patient documents and delivered reports
							</div>
							<div className="db-card__badge">
								<span className="db-card__badge-dot" />
								Unread: {unreadMessagesCount}&nbsp;·&nbsp;Total: {totalMessagesCount}
							</div>
						</Link>
					</Grid>

					<Grid item xs={12} md={6} lg={3}>
						<a
							href={emrURL + "oscar/tickler/ticklerMain.jsp"}
							target="_blank"
							rel="noreferrer"
							className="db-card"
						>
							<div className="db-card__icon">
								<MailOutlineIcon style={{ fontSize: 24, color: "#E47029" }} />
							</div>
							<div className="db-card__title">Inbox (OSCAR Tickler)</div>
							<div className="db-card__sub">Tasks and inbox items</div>
							<div className="db-card__cta">
								Open Inbox <ArrowIcon />
							</div>
						</a>
					</Grid>

					<Grid item xs={12} md={6} lg={3}>
						<a
							href={
								emrURL +
								`oscar/provider/appointmentprovideradminday.jsp?year=${year}&month=${month + 1}&day=${day}`
							}
							target="_blank"
							rel="noreferrer"
							className="db-card"
						>
							<div className="db-card__icon">
								<EventIcon style={{ fontSize: 24, color: "#E47029" }} />
							</div>
							<div className="db-card__title">Daysheet</div>
							<div className="db-card__sub">Today's appointment schedule</div>
							<div className="db-card__cta">
								Open Daysheet <ArrowIcon />
							</div>
						</a>
					</Grid>

					<Grid item xs={12} md={6} lg={3}>
						<Link to="/specialists" className="db-card">
							<div className="db-card__icon">
								<LocalHospitalIcon style={{ fontSize: 24, color: "#E47029" }} />
							</div>
							<div className="db-card__title">Specialists</div>
							<div className="db-card__sub">Browse the referral directory</div>
							<div className="db-card__cta">
								Browse <ArrowIcon />
							</div>
						</Link>
					</Grid>

				</Grid>
			</div>

		</div>
	);
};

export default Dashboard;